import { Inject, Injectable } from '@angular/core';
import * as Rx from 'rxjs';
import { BackendFetchTypes, BackendPushReturnTypes } from '../backend.model';
import { Collections, SearchCollections } from '../collections.model';
import {
  BackendDeleteRequest,
  BackendFetchRequest,
  BackendPushRequest,
  BackendSetRequest,
  BackendUpdateRequest,
  Params,
} from './backend';
import { HTTP_ADAPTER } from './datastore.config';
import { HttpAdapter, ResponseData } from './datastore.interface';
import { Pagination } from './store.model';

export type ApiFetchResponse<
  K extends keyof (Collections & SearchCollections)
> = ResponseData<BackendFetchTypes[K]>;

export type ApiPostResponse<K extends keyof Collections> = ResponseData<
  BackendPushReturnTypes[K]
>;

@Injectable()
export class ApiHttp {
  constructor(@Inject(HTTP_ADAPTER) private httpAdapter: HttpAdapter) {}

  fetch<K extends keyof Collections>(
    request: BackendFetchRequest,
    pagination?: Pagination,
  ): Rx.Observable<ApiFetchResponse<K>>;

  fetch<K extends keyof (Collections & SearchCollections)>(
    request: BackendFetchRequest,
  ): Rx.Observable<ApiFetchResponse<K>>;

  fetch<K extends keyof (Collections & SearchCollections)>(
    request: BackendFetchRequest,
    pagination?: Pagination,
  ): Rx.Observable<ApiFetchResponse<K>> {
    const params = pagination
      ? {
          limit: pagination.limit,
          offset: pagination.offset,
          ...request.params,
        }
      : request.params;

    return this.httpAdapter.get<BackendFetchTypes[K]>(request.endpoint, {
      params,
      isGaf: request.isGaf,
    });
  }

  post<K extends keyof Collections>(
    request:
      | BackendPushRequest<K>
      | BackendSetRequest<K>
      | BackendUpdateRequest<K>
      | BackendDeleteRequest<K>,
  ): Rx.Observable<ApiPostResponse<K>> {
    const body = {
      ...(request.payload as Params), // TODO: https://github.com/Microsoft/TypeScript/issues/15300
      ...request.params,
    };
    const options = {
      isGaf: request.isGaf,
      serializeBody: request.asFormData,
    };
    return this.httpAdapter.post<ApiPostResponse<K>>(
      request.endpoint,
      body,
      options,
    );
  }

  put<K extends keyof Collections>(
    request: BackendUpdateRequest<K> | BackendDeleteRequest<K>,
  ): Rx.Observable<ApiPostResponse<K>> {
    const body = {
      ...(request.payload as Params), // TODO: https://github.com/Microsoft/TypeScript/issues/15300
      ...request.params,
    };
    const options = {
      isGaf: request.isGaf,
      serializeBody: request.asFormData,
    };
    return this.httpAdapter.put<ApiPostResponse<K>>(
      request.endpoint,
      body,
      options,
    );
  }

  delete<K extends keyof Collections>(
    request: BackendDeleteRequest<K>,
  ): Rx.Observable<ApiPostResponse<K>> {
    return this.httpAdapter.delete<BackendPushReturnTypes[K]>(
      request.endpoint,
      {
        isGaf: request.isGaf,
      },
    );
  }
}
